/**
 * Usage & Quota — Sprint BH.
 *
 * The CostDashboard page (Sprint AE) shows historical LLM spend
 * with per-provider / per-agent breakdowns. This page is the
 * billing-side complement: current month's spend vs. the
 * tenant's caps, period boundaries, and a live event feed for
 * the last 200 LLM calls.
 *
 * Admins with the `admin.billing` scope also get an inline
 * editor for the soft/hard caps (PUT /usage/quota). Other roles
 * see read-only.
 *
 * Wires three endpoints:
 *   GET /usage/current  → quota card
 *   GET /usage/events   → events table (filterable by provider)
 *   PUT /usage/quota    → admin cap editor
 */

import { useCallback, useState } from "react";
import {
  AlertTriangle, BadgeCheck, CalendarDays, Coins, Filter, Pencil,
  RefreshCw, X,
} from "lucide-react";
import { Button, Card, CardHeader } from "@vseh/ui";

import {
  usageApi,
  type QuotaDto,
  type UsageEventDto,
  type UsageForecastDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock } from "../shared/components/States";
import { SkeletonTable, SkeletonTiles } from "../shared/components/Skeleton";


export function UsagePage(): JSX.Element {
  const t = useT();
  const [provider, setProvider] = useState<string>("");
  const [editing,  setEditing]  = useState(false);

  const quotaFetcher = useCallback(
    (signal: AbortSignal) => usageApi.current({ signal }),
    [],
  );
  const { data: quota, error: qErr, loading: qLoading, refetch: refetchQuota } =
    useFetch<QuotaDto>(quotaFetcher, []);

  // Sprint H1 — forecast fetch. Independent of the quota fetch
  // so a forecast failure doesn't hide the quota tiles. The
  // forecast endpoint returns a zero-shaped row when there's
  // no quota row yet, so the banner stays harmless on a fresh
  // tenant.
  const forecastFetcher = useCallback(
    (signal: AbortSignal) => usageApi.forecast({ signal }),
    [],
  );
  const { data: forecast } = useFetch<UsageForecastDto>(forecastFetcher, []);

  const eventsFetcher = useCallback(
    (signal: AbortSignal) => {
      // exactOptionalPropertyTypes — only include `provider` when set,
      // don't pass `provider: undefined` which the type forbids.
      const params: { limit?: number; provider?: string } = { limit: 200 };
      if (provider) params.provider = provider;
      return usageApi.events(params, { signal });
    },
    [provider],
  );
  const { data: events, error: eErr, loading: eLoading, refetch: refetchEvents } =
    useFetch<UsageEventDto[]>(eventsFetcher, [provider]);

  return (
    <div className="flex flex-col gap-4">
      {/* ----- Quota card ------------------------------------------------ */}
      <Card>
        <CardHeader
          title={t("usage.page.title")}
          description={t("usage.page.subtitle")}
          actions={
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="xs"
                      leftIcon={<RefreshCw className="size-3.5" />}
                      onClick={() => { refetchQuota(); refetchEvents(); }}>
                {t("common.refresh")}
              </Button>
              {quota && !editing && (
                <Button variant="ghost" size="xs"
                        leftIcon={<Pencil className="size-3.5" />}
                        onClick={() => setEditing(true)}>
                  {t("usage.edit_cap")}
                </Button>
              )}
            </div>
          }
        />
        {qLoading && !quota && <SkeletonTiles count={4} />}
        {qErr && <ErrorBlock error={qErr} onRetry={refetchQuota} />}
        {quota && (
          editing
            ? <QuotaEditor
                quota={quota}
                onCancel={() => setEditing(false)}
                onSaved={() => { setEditing(false); refetchQuota(); }}
              />
            : <QuotaSummary quota={quota} />
        )}
      </Card>

      {/* ----- Forecast banner (Sprint H1) ----------------------------- */}
      {forecast && <ForecastBanner forecast={forecast} />}

      {/* ----- Events feed ----------------------------------------------- */}
      <Card>
        <CardHeader
          title={t("usage.events.title")}
          description={
            events
              ? `${events.length}` +
                (provider ? ` · ${provider}` : "")
              : ""
          }
          actions={
            <ProviderFilter value={provider} onChange={setProvider} />
          }
        />
        {eLoading && !events && <SkeletonTable rows={10} cols={7} />}
        {eErr && <ErrorBlock error={eErr} onRetry={refetchEvents} />}
        {events && <EventsTable rows={events} />}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Quota summary — read-only mode
// ---------------------------------------------------------------------------


function QuotaSummary({ quota }: { quota: QuotaDto }): JSX.Element {
  const t = useT();
  return (
    <div className="grid grid-cols-12 gap-3">
      <Tile
        icon={<Coins className="size-4 text-ai-600" />}
        label={t("usage.tile.spent")}
        primary={`$${quota.current_month_usd.toFixed(2)}`}
        secondary={
          quota.over_hard ? t("usage.tile.spent.over_hard") :
          quota.over_soft ? t("usage.tile.spent.over_soft") :
                            t("usage.tile.spent.ok")
        }
        tone={
          quota.over_hard ? "danger" :
          quota.over_soft ? "warn"   : "ok"
        }
      />
      <Tile
        icon={<BadgeCheck className="size-4 text-base-500" />}
        label={t("usage.tile.soft")}
        primary={quota.monthly_soft_usd == null
          ? "—"
          : `$${quota.monthly_soft_usd.toFixed(2)}`}
        secondary={t("usage.tile.soft.hint")}
      />
      <Tile
        icon={<AlertTriangle className="size-4 text-base-500" />}
        label={t("usage.tile.hard")}
        primary={quota.monthly_hard_usd == null
          ? "—"
          : `$${quota.monthly_hard_usd.toFixed(2)}`}
        secondary={t("usage.tile.hard.hint")}
      />
      <Tile
        icon={<CalendarDays className="size-4 text-base-500" />}
        label={t("usage.tile.period")}
        primary={fmtPeriod(quota.period_start, quota.period_end)}
        secondary={t("usage.tile.period.hint")}
      />
    </div>
  );
}


function Tile(props: {
  icon: JSX.Element;
  label: string;
  primary: string;
  secondary: string;
  tone?: "ok" | "warn" | "danger";
}): JSX.Element {
  const toneCls =
    props.tone === "danger" ? "text-danger-600" :
    props.tone === "warn"   ? "text-warning-600" :
                              "text-base-900";
  return (
    <div className="col-span-3 rounded-sm border border-base-200 bg-surface px-3 py-2">
      <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide text-base-500">
        {props.icon}
        {props.label}
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${toneCls}`}>
        {props.primary}
      </div>
      <div className="text-2xs text-base-500">{props.secondary}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Quota editor — admin.billing only
// ---------------------------------------------------------------------------


function QuotaEditor(props: {
  quota:    QuotaDto;
  onCancel: () => void;
  onSaved:  () => void;
}): JSX.Element {
  const t = useT();
  const [soft, setSoft] = useState<string>(
    props.quota.monthly_soft_usd == null
      ? "" : props.quota.monthly_soft_usd.toString(),
  );
  const [hard, setHard] = useState<string>(
    props.quota.monthly_hard_usd == null
      ? "" : props.quota.monthly_hard_usd.toString(),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await usageApi.updateQuota({
        monthly_soft_usd: soft === "" ? null : Number(soft),
        monthly_hard_usd: hard === "" ? null : Number(hard),
      });
      props.onSaved();
    } catch (e) {
      // Surface server-side validation (e.g. soft > hard returns 422).
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-12 gap-3">
        <CapInput
          label={t("usage.cap.soft.label")}
          hint={t("usage.cap.soft.hint")}
          value={soft}
          onChange={setSoft}
        />
        <CapInput
          label={t("usage.cap.hard.label")}
          hint={t("usage.cap.hard.hint")}
          value={hard}
          onChange={setHard}
        />
      </div>
      {err && (
        <div className="rounded-sm border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">
          {err}
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm"
                leftIcon={<X className="size-3.5" />}
                onClick={props.onCancel} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" size="sm"
                onClick={save} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}


function CapInput(props: {
  label: string;
  hint:  string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <label className="col-span-6 flex flex-col gap-1">
      <span className="text-2xs font-medium uppercase tracking-wide text-base-600">
        {props.label}
      </span>
      <input
        type="number" min={0} step={0.01}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={t("usage.cap.placeholder")}
        className="rounded-sm border border-base-300 bg-surface px-3 py-1.5 font-mono text-sm tabular-nums focus:border-primary-500 focus:outline-none"
      />
      <span className="text-2xs text-base-500">{props.hint}</span>
    </label>
  );
}


// ---------------------------------------------------------------------------
// Events table
// ---------------------------------------------------------------------------


function EventsTable({ rows }: { rows: UsageEventDto[] }): JSX.Element {
  const t = useT();
  if (rows.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-base-500">
        {t("common.no_data")}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs tabular-nums">
        <thead className="text-2xs uppercase tracking-wide text-base-500">
          <tr className="border-b border-base-200">
            <th className="px-2 py-1 text-left">{t("usage.events.col.time")}</th>
            <th className="px-2 py-1 text-left">{t("usage.events.col.model")}</th>
            <th className="px-2 py-1 text-left">{t("usage.events.col.task")}</th>
            <th className="px-2 py-1 text-right">{t("usage.events.col.tokens")}</th>
            <th className="px-2 py-1 text-right">{t("usage.events.col.cost")}</th>
            <th className="px-2 py-1 text-right">{t("usage.events.col.latency")}</th>
            <th className="px-2 py-1 text-left">{t("usage.events.col.agent")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.event_id} className="border-b border-base-100 hover:bg-base-50">
              <td className="px-2 py-1 font-mono text-base-700">
                {fmtTime(r.occurred_at)}
              </td>
              <td className="px-2 py-1">
                <span className="text-base-700">{r.provider}</span>{" · "}
                <span className="font-mono text-base-500">{r.model}</span>
              </td>
              <td className="px-2 py-1 text-base-700">{r.task_type}</td>
              <td className="px-2 py-1 text-right text-base-700">
                {r.input_tokens.toLocaleString("ru-RU")}/{r.output_tokens.toLocaleString("ru-RU")}
              </td>
              <td className="px-2 py-1 text-right font-medium text-base-900">
                ${r.cost_usd.toFixed(6)}
              </td>
              <td className="px-2 py-1 text-right text-base-600">
                {r.latency_ms == null ? "—" : `${r.latency_ms} ${t("usage.events.unit.ms")}`}
              </td>
              <td className="px-2 py-1 font-mono text-base-500">
                {r.agent_id ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ProviderFilter(props: {
  value: string; onChange: (v: string) => void;
}): JSX.Element {
  const t = useT();
  const options = ["", "openai", "anthropic", "ollama", "stub"];
  return (
    <div className="flex items-center gap-2">
      <Filter className="size-3.5 text-base-500" />
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="rounded-sm border border-base-300 bg-surface px-2 py-1 text-xs"
      >
        {options.map((o) => (
          <option key={o || "all"} value={o}>{o || t("usage.filter.providers.all")}</option>
        ))}
      </select>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------


function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}


function fmtPeriod(start: string, end: string): string {
  const a = new Date(start);
  const b = new Date(end);
  return `${a.toLocaleDateString("ru-RU")} – ${b.toLocaleDateString("ru-RU")}`;
}


// ---------------------------------------------------------------------------
// Sprint H1 — month-end forecast banner
// ---------------------------------------------------------------------------


/**
 * Compact banner under the quota card showing the linear
 * month-end projection. Three tones:
 *
 *   info   — under both caps, or no caps set
 *   warn   — will exceed soft cap by month-end
 *   danger — will exceed hard cap by month-end (the actual
 *            production blocker, displayed prominently)
 *
 * When days_elapsed is 1, the banner appends a "day-1
 * projection — likely noisy" hint so an operator doesn't
 * read a single noisy spike as a settled trend. The server-
 * side docstring (api/usage.py:UsageForecastDto) explains
 * the no-smoothing choice; this UI surface honestly reflects
 * it.
 */
function ForecastBanner({ forecast }: { forecast: UsageForecastDto }): JSX.Element {
  const t = useT();

  const tone =
    forecast.projected_over_hard ? "danger" :
    forecast.projected_over_soft ? "warn"   : "info";

  const bg = {
    danger: "border-danger-200 bg-danger-50  text-danger-800",
    warn:   "border-warning-200 bg-warning-50 text-warning-800",
    info:   "border-base-200 bg-base-50 text-base-700",
  }[tone];

  // Sprint I2 — prefer the smoothed projection when available.
  // The server's `projected_over_*` booleans are already
  // computed against the smoothed value when it exists, so the
  // headline number matches what the threshold check used.
  const headline =
    forecast.projected_month_usd_smoothed != null
      ? forecast.projected_month_usd_smoothed
      : forecast.projected_month_usd;

  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${bg}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">
          {t("usage.forecast.label")}
        </span>
        <span className="font-mono tabular-nums">
          ${headline.toFixed(2)}
        </span>
        <span className="text-2xs opacity-75">
          {t("usage.forecast.days", {
            elapsed: forecast.days_elapsed,
            total:   forecast.days_in_period,
          })}
        </span>
        <span className="text-2xs opacity-75">
          {forecast.smoothing_method === "median_7d"
            ? t("usage.forecast.method.median_7d")
            : t("usage.forecast.method.linear")}
        </span>
        {forecast.smoothing_method === "median_7d" &&
         forecast.projected_month_usd !== forecast.projected_month_usd_smoothed && (
          <span className="text-2xs opacity-75 font-mono tabular-nums">
            {t("usage.forecast.linear_was", {
              value: forecast.projected_month_usd.toFixed(2),
            })}
          </span>
        )}
        {forecast.projected_over_hard && forecast.monthly_hard_usd != null && (
          <span className="text-xs font-medium">
            {t("usage.forecast.over_hard", {
              cap: forecast.monthly_hard_usd.toFixed(2),
            })}
          </span>
        )}
        {!forecast.projected_over_hard && forecast.projected_over_soft &&
         forecast.monthly_soft_usd != null && (
          <span className="text-xs font-medium">
            {t("usage.forecast.over_soft", {
              cap: forecast.monthly_soft_usd.toFixed(2),
            })}
          </span>
        )}
        {forecast.days_elapsed <= 1 && (
          <span className="text-2xs opacity-75">
            {t("usage.forecast.day1_hint")}
          </span>
        )}
      </div>
      {/* L-phase — anomaly row. Rendered only when the detector
        * fired; the server makes the call (server-side MAD over
        * the same 7-day rollup the smoother uses), and we just
        * surface the supporting numbers so the operator can see
        * *why* it tripped without a second click. The row sits
        * below the main forecast line so an unrelated spike
        * doesn't visually compete with the projection itself.
        */}
      {forecast.anomaly_detected &&
       forecast.anomaly_spike_usd != null &&
       forecast.anomaly_baseline_usd != null && (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-xs">
          <span className="font-medium text-warning-800">
            {t("usage.forecast.anomaly.label")}
          </span>
          <span className="font-mono tabular-nums">
            {t("usage.forecast.anomaly.today", {
              value: forecast.anomaly_spike_usd.toFixed(2),
            })}
          </span>
          <span className="opacity-75 font-mono tabular-nums">
            {t("usage.forecast.anomaly.baseline", {
              value: forecast.anomaly_baseline_usd.toFixed(2),
            })}
          </span>
        </div>
      )}
    </div>
  );
}
